import React, { useEffect, useState } from 'react';
import { Dialog, DialogTitle, DialogContent, DialogActions, makeStyles, Theme } from '@material-ui/core';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { AddInvestorToNetworkResponse } from '../../types/types';
import Button from '../../components/Button';
import TextInput from '../../components/TextInput';
import Form from '../../components/Form';
import { StyledText } from '../../components/StyledText';
import api from '../../api/api';
import { useAlert, useUser } from '../../contexts';
import { formikSubmitDisabled } from '../../lib/utils';

export const newInvestorCreatedMessage = 'A new profile was created. Please finish filling out the new profile.';
const initialValues = {
  email: ''
};

const validationSchema = Yup.object().shape({
  email: Yup.string().email().required()
});

const useStyles = makeStyles((theme: Theme) => ({
  root: {
    margin: theme.spacing(0),
    minWidth: 400
  },
  dialogActions: {
    display: 'flex',
    justifyContent: 'space-between',
    padding: '10px 24px'
  },
  warningLabel: {
    verticalAlign: 'bottom',
    display: 'inline-flex'
  }
}));

interface InvestorOnboardProps {
  open: boolean
  goToProfile: (id?: number) => void
  handleClose: () => void
}

interface FinishedResultViewProps {
  addInvestorState: AddInvestorToNetworkResponse,
  goToProfile: () => void
}
const FinishedResultView:React.FC<FinishedResultViewProps> = ({
  addInvestorState,
  goToProfile
}) => {
  const styles = useStyles();
  const makeText = () => (addInvestorState.networkRelationshipCreated
    ? 'We found an existing investor profile with the email you provided and added it to your network.'
    : 'Looks like that investor was already in your network.');

  return (
    <>
      <DialogTitle disableTypography>
        <StyledText variant='h2'>{'You\'re connected'}</StyledText>
      </DialogTitle>
      <DialogContent>
        <StyledText>
          {makeText()}
        </StyledText>
      </DialogContent>
      <DialogActions className={styles.dialogActions}>
        <Button disabled style={{ opacity: 0 }}>Close</Button>
        <Button onClick={goToProfile}>View profile</Button>
      </DialogActions>
    </>
  );
};

interface DeletedInvestorViewProps{
  addInvestorState: AddInvestorToNetworkResponse
}
const DeletedInvestorView:React.FC<DeletedInvestorViewProps> = () => {
  const styles = useStyles();
  return (
    <>
      <DialogTitle disableTypography>
        <StyledText variant='h2'>Sorry, there was a problem adding this investor.</StyledText>
      </DialogTitle>
      <DialogContent>
        <StyledText display='inline'>
          This investor email is associated with a disabled account. Please contact&nbsp;
        </StyledText>
        <StyledText display='inline' color='primary'>
          support@ventureraise.io&nbsp;
        </StyledText>
        <StyledText display='inline'>
          if you have any questions.
        </StyledText>
      </DialogContent>
      <DialogActions className={styles.dialogActions}>
        <Button disabled style={{ opacity: 0 }}>Close</Button>
      </DialogActions>
    </>
  );
};
const InvestorOnboard: React.FC<InvestorOnboardProps> = ({
  open, goToProfile, handleClose
}) => {
  const [addInvestorState, setAddInvestorState] = useState<AddInvestorToNetworkResponse>();
  const { getAccessToken } = useUser();
  const { postNewAlert } = useAlert();

  const handleVPClick = () => {
    if (!addInvestorState) { return; }
    const { profileId } = addInvestorState;
    // being a little lazy with the types here but it will always be one or the other
    goToProfile(profileId);
  };

  useEffect(() => {
    if (!open) {
      formik.resetForm();
      setAddInvestorState(undefined);
    }
  // formik is only missing dependency here and we all know what that does
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const onSubmit = async (values: { email: string }) => {
    try {
      const token = await getAccessToken();
      const addResponse = await api.addInvestorToNetwork(token, values);
      console.log('add investor response', addResponse);

      if (addResponse.unpublished) {
        let message = addResponse.newProfileCreated ? newInvestorCreatedMessage : 'We added an existing investor to your network. Please help finish their profile.';
        if (!addResponse.networkRelationshipCreated) {
          message = 'This investor was already in your network but has not been published.';
        }
        goToProfile(addResponse.profileId);
        postNewAlert(message);
      }

      setAddInvestorState(addResponse);
    } catch (e: unknown) {
      console.log('error adding investor to network', e);
      postNewAlert('An error occurred while adding the investor to your network.');
    }
  };

  const styles = useStyles();
  const formik = useFormik({
    initialValues,
    enableReinitialize: true,
    validationSchema,
    onSubmit
  });


  return (
    <Dialog
      open={open}
      className={styles.root}
      onClose={handleClose}
    >
      <Form formik={formik}>
        {
          (addInvestorState)
            ? (
              <>
                {
                (addInvestorState.isDeleted)
                  ? <DeletedInvestorView addInvestorState={addInvestorState} />
                  : (
                    <FinishedResultView addInvestorState={addInvestorState} goToProfile={handleVPClick} />
                  )
                }
              </>
            ) : (
              <>
                { open ? (
                  <>
                    <DialogTitle disableTypography>
                      <StyledText variant='h2'>Expand your network</StyledText>
                    </DialogTitle>
                    <DialogContent>
                      <StyledText>
                        {'Enter the investor\'s email.  We\'ll match or create a new investor profile.'}
                      </StyledText>
                      <TextInput
                        type='text'
                        fullWidth
                        id='email'
                        label='Email'
                        placeholder=''
                      />
                    </DialogContent>
                    <DialogActions className={styles.dialogActions}>
                      <Button variant='text' type='button' onClick={handleClose}>Cancel</Button>
                      <Button type='submit' disabled={formikSubmitDisabled(formik)}>Add investor</Button>
                    </DialogActions>
                  </>
                ) : null }
              </>
            )
        }
      </Form>
    </Dialog>
  );
};

export default InvestorOnboard;

